import { Link } from "react-router-dom";
import { Building, MapPin, Mail, Globe, FileText } from "lucide-react";
import SEO from "@/components/SEO";

const CompanyInfoPage = () => {
  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO 
        title="Company Information - Legal & Registration Details"
        description="Magnus Kor Talent Partners operates under L&D Nexus Limited (UK, Company No. 16450617). View company registration details and office location."
        keywords="L&D Nexus, company registration UK, Magnus Kor company info, talent advisory firm registration, London office, company details"
        canonical="https://magnuskor.com/company-info"
        noIndex={true}
      />

      {/* Header */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-8 md:px-16">
          <span className="text-[#B8860B] text-xs uppercase tracking-[0.2em] font-medium">Legal Information</span>
          <h1 className="font-display text-4xl md:text-5xl mt-4 mb-6">Company Information</h1>
          <p className="text-gray-600 text-lg">
            Magnus Kor Talent Partners is a specialized recruitment division operating under L&D Nexus, 
            with registered offices in the United Kingdom.
          </p>
        </div>
      </section>

      {/* Company Structure */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* UK Office */}
            <div className="bg-white p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#B8860B]/10 rounded-full flex items-center justify-center">
                  <Building size={20} className="text-[#B8860B]" />
                </div>
                <div>
                  <p className="text-xs text-[#B8860B] uppercase tracking-wider">Registered Office</p>
                  <h3 className="font-display text-xl">United Kingdom</h3>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Company Name</p>
                  <p className="font-medium">L&D Nexus Limited</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Company Number</p>
                  <p className="font-medium">16450617</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Registered Address</p>
                  <p className="text-gray-700">
                    71-75 Shelton Street<br />
                    Covent Garden<br />
                    London, WC2H 9JQ<br />
                    United Kingdom
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Additional Info */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-8 md:px-16">
          <h2 className="font-display text-2xl mb-8">Contact Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-start gap-4">
              <Mail size={20} className="text-[#B8860B] mt-1" />
              <div>
                <p className="text-sm text-gray-500 mb-1">General Enquiries</p>
                <a href="mailto:office@magnuskor.com" className="text-[#B8860B] hover:underline">
                  office@magnuskor.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail size={20} className="text-[#B8860B] mt-1" />
              <div>
                <p className="text-sm text-gray-500 mb-1">Hiring Inquiries</p>
                <a href="mailto:recruitment@magnuskor.com" className="text-[#B8860B] hover:underline">
                  recruitment@magnuskor.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Magnus Kor */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-8 md:px-16">
          <h2 className="font-display text-2xl text-white mb-6">About Magnus Kor Talent Partners</h2>
          <p className="text-gray-400 leading-relaxed mb-6">
            Magnus Kor Talent Partners is a specialized recruitment division operating under L&D Nexus, 
            focused on building core teams for VC-backed SaaS startups. We provide talent advisory services, 
            from individual contributor roles to executive leadership positions, across Engineering, Product, 
            Growth, Sales, and Customer Success functions.
          </p>
          <p className="text-gray-400 leading-relaxed mb-8">
            Operating under L&D Nexus Limited (UK), we combine 
            global reach with deep expertise in the startup ecosystem to help high-growth companies build 
            exceptional teams.
          </p>
          <Link 
            to="/about" 
            className="inline-flex items-center gap-2 text-[#B8860B] hover:text-[#D4A84B] transition-colors"
          >
            Learn more about our story
            <FileText size={16} />
          </Link>
        </div>
      </section>

      {/* Legal Links */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-8 md:px-16">
          <div className="flex flex-wrap gap-6 text-sm">
            <Link to="/privacy" className="text-gray-500 hover:text-[#B8860B] transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-500 hover:text-[#B8860B] transition-colors">
              Terms of Service
            </Link>
            <Link to="/contact" className="text-gray-500 hover:text-[#B8860B] transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyInfoPage;
